import { isTimePermissionEnabled } from '../../config/applicationFeatures';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { useFocusEffect } from '@react-navigation/native';
import type {
  NativeStackNavigationProp,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getReportModuleCapabilities,
  isReportGranted,
  REPORT_ACCESS_CODES,
  REPORT_ACCESS_PERMISSIONS,
} from '../../config/reportCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { useReportAccess } from '../../hooks/useReportAccess';
import { getDepartments } from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import {
  runReportPreview,
  runTeamAttendanceRegisterPreview,
} from '../../services/reportService';
import type {
  AttendanceReportFilters,
  AttendanceReportRow,
} from '../../types/attendanceReport';
import type { DashboardScope } from '../../types/dashboard';
import { canViewTeamDashboard } from '../../utils/approvalRoles';
import {
  attendanceRegisterFilters,
  createDefaultAttendanceReportFilters,
  filterAttendanceReportRows,
  formatAttendanceReportDateLabel,
  formatPunchTimeWithSource,
  mapAttendanceReportRows,
  summarizeAttendanceReportRows,
  validateAttendanceReportDate,
} from '../../utils/attendanceReport';
import {
  parseCalendarDateInput,
  toCalendarDateString,
} from '../../utils/dateTime';
import { reportErrorMessage } from '../../utils/reportErrors';
import { formatStatusWithNotes } from '../../utils/attendanceDayStatus';
import { trackingStatusLabel } from '../../utils/locationTracking';

type StatusChipId =
  | 'all'
  | 'present'
  | 'absent'
  | 'late'
  | 'leave'
  | 'wfh'
  | 'shortHours'
  | 'missingCheckout';

type SummaryMetric = {
  key: StatusChipId;
  label: string;
  value: number;
  color: string;
  chip: StatusChipId;
};

type LocationOption = { id: number; label: string };
type DepartmentOption = {
  id: number;
  label: string;
  locationId: number | null;
};

type Props = (
  | NativeStackScreenProps<RootStackParamList, 'AttendanceReport'>
  | BottomTabScreenProps<MainTabParamList, 'AttendanceReport'>
  | NativeStackScreenProps<RootStackParamList, 'TeamAttendanceReport'>
) & {
  /**
   * `organization` (default) is the HR Admin register. `team` shows the same
   * register for a Team Lead, limited by the backend to assigned employees.
   */
  scope?: DashboardScope;
};

const REPORT_CODE = 'ATTENDANCE_REGISTER';
const REPORT_NAME = 'Daily Attendance';
const TEAM_REPORT_NAME = 'Team Attendance';
const PREVIEW_MAX_ROWS = 500;
const MASTER_DATA_PAGE_SIZE = 100;
/** Ignore extra tab focuses while a preview is in flight or just finished. */
const FOCUS_RELOAD_COOLDOWN_MS = 2000;

const COL_EMPLOYEE = 200;
const COL_STATUS = 150;
const COL_TIME = 200;
const COL_HOURS = 100;
const COL_DETAILS = 220;

const STATUS_CHIP_FILTER: Record<StatusChipId, string> = {
  all: '',
  present: 'Present',
  absent: 'LOP',
  late: 'Late',
  leave: 'On Leave',
  wfh: 'WFH',
  shortHours: 'Short Hours',
  missingCheckout: 'Missing Checkout',
};

function isMoreChip(chip: StatusChipId): boolean {
  return (
    chip === 'leave' ||
    chip === 'late' ||
    chip === 'wfh' ||
    chip === 'shortHours' ||
    chip === 'missingCheckout'
  );
}

function chipFromStatus(status: string): StatusChipId {
  const normalized = status.trim().toLowerCase();
  if (!normalized) return 'all';
  if (normalized === 'present') return 'present';
  if (normalized === 'absent' || normalized === 'lop') return 'absent';
  if (normalized === 'late') return 'late';
  if (normalized === 'on leave' || normalized === 'leave') return 'leave';
  if (normalized === 'wfh') return 'wfh';
  if (normalized === 'short hours' || normalized === 'shorthours') {
    return 'shortHours';
  }
  if (normalized === 'missing checkout' || normalized === 'missing check out') {
    return 'missingCheckout';
  }
  return 'all';
}

function totalHoursColor(
  row: AttendanceReportRow,
  palette: ColorPalette,
): string {
  if (row.totalHours === '—' || row.totalHoursCovered == null) {
    return palette.textSecondary;
  }
  if (row.totalHoursCovered === false) return palette.error;
  return palette.textSecondary;
}

function workHoursColor(
  row: AttendanceReportRow,
  palette: ColorPalette,
): string {
  if (!row.hasCheckedOut || row.workHours === '—') {
    return palette.textSecondary;
  }
  if (row.hoursCovered === true) return palette.success;
  if (row.hoursCovered === false || row.isShortHours) return palette.error;
  return palette.textSecondary;
}

function statusColor(
  tone: AttendanceReportRow['statusTone'],
  palette: ColorPalette,
): string {
  switch (tone) {
    case 'present':
      return palette.success;
    case 'absent':
      return palette.error;
    case 'late':
      return palette.error;
    case 'leave':
      return palette.warning;
    case 'wfh':
      return palette.info;
    case 'shortHours':
      return palette.warning;
    default:
      return palette.textSecondary;
  }
}

function shiftAttendanceDate(
  attendanceDate: string,
  deltaDays: number,
): string {
  const parsed = parseCalendarDateInput(attendanceDate);
  if (!parsed) return attendanceDate;
  parsed.setDate(parsed.getDate() + deltaDays);
  return toCalendarDateString(parsed);
}

function FilterIcon({ color }: { color: string }) {
  return (
    <View style={styles.filterIcon} accessibilityElementsHidden>
      <View
        style={[
          styles.filterLine,
          styles.filterWide,
          { backgroundColor: color },
        ]}
      />
      <View
        style={[
          styles.filterLine,
          styles.filterMid,
          { backgroundColor: color },
        ]}
      />
      <View
        style={[
          styles.filterLine,
          styles.filterNarrow,
          { backgroundColor: color },
        ]}
      />
    </View>
  );
}

function ChevronIcon({
  color,
  direction,
}: {
  color: string;
  direction: 'left' | 'right';
}) {
  return (
    <Text style={[styles.chevron, { color }]}>
      {direction === 'left' ? '‹' : '›'}
    </Text>
  );
}

function SearchIcon({ color }: { color: string }) {
  return (
    <View style={styles.searchIcon} accessibilityElementsHidden>
      <View style={[styles.searchCircle, { borderColor: color }]} />
      <View style={[styles.searchHandle, { backgroundColor: color }]} />
    </View>
  );
}

function attendanceRowAccessibilityLabel(row: AttendanceReportRow): string {
  const checkInDisplay = formatPunchTimeWithSource(
    row.checkIn,
    row.checkInSourceLabel,
  );
  const checkOutDisplay = formatPunchTimeWithSource(
    row.checkOut,
    row.checkOutSourceLabel,
  );
  return `${row.employee}, check in ${checkInDisplay}, check out ${checkOutDisplay}, work hours ${row.workHours}, break ${row.breakTime}, expected work hours ${row.expectedHours}, total work hours ${row.totalHours}, ${row.status}${row.dayDetails !== '—' ? `, ${row.dayDetails.replace(/\n/g, ', ')}` : ''}`;
}

function AttendanceEmployeeCell({
  row,
  palette,
}: {
  row: AttendanceReportRow;
  palette: ColorPalette;
}) {
  return (
    <View style={[styles.employeeCell, { width: COL_EMPLOYEE }]}>
      <ProfileAvatar
        name={row.employee === '—' ? '?' : row.employee}
        size={36}
      />
      <Text
        style={[styles.employeeName, { color: palette.textPrimary }]}
        numberOfLines={2}
      >
        {row.employee}
      </Text>
    </View>
  );
}

/** Tracked client visit the API allows this viewer to open. */
function TrackingDetailsLink({
  row,
  palette,
  onOpenTracking,
}: {
  row: AttendanceReportRow;
  palette: ColorPalette;
  onOpenTracking?: (attendanceId: number) => void;
}) {
  const attendanceId = row.trackingAttendanceId;
  if (!onOpenTracking || !attendanceId) return null;
  const status = trackingStatusLabel(row.trackingStatus);
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={`Tracking Details for ${row.employee}${status ? `, ${status}` : ''}`}
      onPress={() => onOpenTracking(attendanceId)}
      hitSlop={6}
      testID={`tracking-details-${row.rowKey}`}
    >
      <Text style={[styles.trackingLink, { color: palette.primary }]}>Tracking Details</Text>
      {status ? (
        <Text style={[styles.trackingStatus, { color: palette.textMuted }]}>{status}</Text>
      ) : null}
    </Pressable>
  );
}

function AttendanceMetricsCells({
  row,
  palette,
  onOpenTracking,
}: {
  row: AttendanceReportRow;
  palette: ColorPalette;
  onOpenTracking?: (attendanceId: number) => void;
}) {
  const checkInDisplay = formatPunchTimeWithSource(
    row.checkIn,
    row.checkInSourceLabel,
  );
  const checkOutDisplay = formatPunchTimeWithSource(
    row.checkOut,
    row.checkOutSourceLabel,
  );
  return (
    <>
      <View style={styles.timeCell}>
        <Text
          style={[
            styles.cellText,
            { width: COL_TIME, color: palette.textSecondary },
          ]}
          numberOfLines={3}
        >
          {checkInDisplay}
        </Text>
        <TrackingDetailsLink row={row} palette={palette} onOpenTracking={onOpenTracking} />
      </View>
      <Text
        style={[
          styles.cellText,
          {
            width: COL_TIME,
            color: row.highlightCheckOutSource
              ? palette.error
              : palette.textSecondary,
            fontWeight: row.highlightCheckOutSource ? '700' : undefined,
          },
        ]}
        numberOfLines={3}
      >
        {checkOutDisplay}
      </Text>
      <Text
        style={[
          styles.cellText,
          { width: COL_HOURS, color: workHoursColor(row, palette) },
        ]}
      >
        {row.workHours}
      </Text>
      <Text
        style={[
          styles.cellText,
          { width: COL_HOURS, color: palette.textSecondary },
        ]}
      >
        {row.breakTime}
      </Text>
      <Text
        style={[
          styles.cellText,
          { width: COL_HOURS, color: palette.textSecondary },
        ]}
      >
        {row.expectedHours}
      </Text>
      <Text
        style={[
          styles.cellText,
          { width: COL_HOURS, color: totalHoursColor(row, palette) },
        ]}
      >
        {row.totalHours}
      </Text>
      <Text
        style={[
          styles.cellText,
          styles.statusText,
          { width: COL_STATUS, color: statusColor(row.statusTone, palette) },
        ]}
        numberOfLines={3 + row.statusNotes.length}
      >
        {formatStatusWithNotes(row.status, row.statusNotes)}
      </Text>
      <Text
        style={[
          styles.cellText,
          { width: COL_DETAILS, color: palette.textSecondary },
        ]}
        numberOfLines={4}
      >
        {row.dayDetails}
      </Text>
    </>
  );
}

/**
 * Employee Name stays fixed on the left; remaining columns scroll horizontally.
 * Row heights are synced so frozen and scrollable panes stay aligned.
 */
function AttendanceReportTable({
  rows,
  palette,
  headerCellStyle,
  onOpenTracking,
}: {
  rows: AttendanceReportRow[];
  palette: ColorPalette;
  onOpenTracking?: (attendanceId: number) => void;
  headerCellStyle: {
    fontWeight: '700';
    color: string;
  };
}) {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
  const rowsIdentity = rows.map(row => row.rowKey).join('\0');

  useEffect(() => {
    setHeaderHeight(0);
    setRowHeights({});
  }, [rowsIdentity]);

  const noteHeaderHeight = useCallback((height: number) => {
    setHeaderHeight(current => (height > current ? height : current));
  }, []);

  const noteRowHeight = useCallback((rowKey: string, height: number) => {
    setRowHeights(current => {
      const previous = current[rowKey] ?? 0;
      if (height <= previous) {
        return current;
      }
      return { ...current, [rowKey]: height };
    });
  }, []);

  const leaveHeaderLabel = isTimePermissionEnabled()
    ? 'Leave / Permission'
    : 'Leave';

  return (
    <View style={styles.tableFrame}>
      <View
        style={[
          styles.frozenPane,
          {
            width: COL_EMPLOYEE,
            backgroundColor: palette.surface,
            borderRightColor: palette.border,
          },
        ]}
      >
        <View
          onLayout={event => noteHeaderHeight(event.nativeEvent.layout.height)}
          style={[
            styles.headerRowTable,
            {
              backgroundColor: palette.background,
              minHeight: headerHeight || undefined,
            },
          ]}
        >
          <Text
            style={[
              headerCellStyle,
              styles.headerCellPad,
              { width: COL_EMPLOYEE },
            ]}
            accessibilityRole="header"
          >
            Employee
          </Text>
        </View>
        {rows.map((row, index) => {
          const isLast = index === rows.length - 1;
          const minHeight = rowHeights[row.rowKey] || undefined;
          return (
            <View
              key={`employee-${row.rowKey}`}
              onLayout={event =>
                noteRowHeight(row.rowKey, event.nativeEvent.layout.height)
              }
              style={[
                styles.bodyRow,
                {
                  borderBottomColor: palette.border,
                  borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
                  minHeight,
                  backgroundColor: palette.surface,
                },
              ]}
              accessibilityLabel={attendanceRowAccessibilityLabel(row)}
            >
              <AttendanceEmployeeCell row={row} palette={palette} />
            </View>
          );
        })}
      </View>
      <ScrollView
        horizontal
        style={styles.scrollPane}
        accessibilityLabel="Employee attendance table"
      >
        <View>
          <View
            onLayout={event => noteHeaderHeight(event.nativeEvent.layout.height)}
            style={[
              styles.headerRowTable,
              {
                backgroundColor: palette.background,
                minHeight: headerHeight || undefined,
              },
            ]}
          >
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_TIME },
              ]}
              accessibilityRole="header"
            >
              Check In
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_TIME },
              ]}
              accessibilityRole="header"
            >
              Check Out
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_HOURS },
              ]}
              accessibilityRole="header"
            >
              Work Hours
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_HOURS },
              ]}
              accessibilityRole="header"
            >
              Break
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_HOURS },
              ]}
              accessibilityRole="header"
            >
              Expected Work Hours
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_HOURS },
              ]}
              accessibilityRole="header"
            >
              Total Work Hours
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_STATUS },
              ]}
              accessibilityRole="header"
            >
              Status
            </Text>
            <Text
              style={[
                headerCellStyle,
                styles.headerCellPad,
                { width: COL_DETAILS },
              ]}
              accessibilityRole="header"
            >
              {leaveHeaderLabel}
            </Text>
          </View>
          {rows.map((row, index) => {
            const isLast = index === rows.length - 1;
            const minHeight = rowHeights[row.rowKey] || undefined;
            return (
              <View
                key={`metrics-${row.rowKey}`}
                onLayout={event =>
                  noteRowHeight(row.rowKey, event.nativeEvent.layout.height)
                }
                style={[
                  styles.bodyRow,
                  {
                    borderBottomColor: palette.border,
                    borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth,
                    minHeight,
                  },
                ]}
              >
                <AttendanceMetricsCells
                  row={row}
                  palette={palette}
                  onOpenTracking={onOpenTracking}
                />
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}

export function AttendanceReportScreen({
  navigation,
  route,
  scope = 'organization',
}: Props) {
  const isTeam = scope === 'team';
  const palette = useAppColors();
  const auth = useAuth();
  const reportCapabilities = getReportModuleCapabilities(auth.permissions);
  /** Team Leads preview their team register only; export stays HR-only. */
  const capabilities = isTeam
    ? {
        ...reportCapabilities,
        canPreview: canViewTeamDashboard(auth.roles, auth.permissions),
        canExportCsv: false,
        canExportXlsx: false,
      }
    : reportCapabilities;
  const downloads = useReportDownloads();
  const hasPermissionAccess = isTeam
    ? canViewTeamDashboard(auth.roles, auth.permissions)
    : auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) &&
      auth.hasAnyPermission([...REPORT_ACCESS_PERMISSIONS]);
  // Roles granted this report in rpt_role_report_access (e.g. Employee) can
  // open it without EMPLOYEE_VIEW; the API then returns only their own rows.
  const roleReportAccess = useReportAccess(
    !isTeam &&
      !hasPermissionAccess &&
      auth.hasAnyPermission([...REPORT_ACCESS_PERMISSIONS]),
  );

  const defaults = useMemo(() => createDefaultAttendanceReportFilters(), []);
  const [draft, setDraft] = useState<AttendanceReportFilters>(() => {
    const initialStatus = route.params?.initialStatus?.trim();
    return initialStatus ? { ...defaults, status: initialStatus } : defaults;
  });
  const [applied, setApplied] = useState<AttendanceReportFilters>(() => {
    const initialStatus = route.params?.initialStatus?.trim();
    return initialStatus ? { ...defaults, status: initialStatus } : defaults;
  });

  const [rows, setRows] = useState<AttendanceReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showMoreChips, setShowMoreChips] = useState(() =>
    isMoreChip(chipFromStatus(route.params?.initialStatus ?? '')),
  );
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [openPicker, setOpenPicker] = useState<
    'department' | 'location' | null
  >(null);
  const [selectedDepartmentLabel, setSelectedDepartmentLabel] = useState<
    string | null
  >(null);
  const [selectedLocation, setSelectedLocation] =
    useState<LocationOption | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<
    DepartmentOption[]
  >([]);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const hasFocused = useRef(false);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;
  const mountedRef = useRef(true);
  const fetchGeneration = useRef(0);
  const inFlightKey = useRef<string | null>(null);
  const lastSuccessAt = useRef(0);

  const canGoBack =
    typeof navigation.canGoBack === 'function' && navigation.canGoBack();

  const themed = useMemo(
    () => ({
      error: {
        ...typography.body,
        color: palette.error,
        marginTop: spacing.sm,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.sm,
      },
      headerCell: {
        ...typography.caption,
        fontWeight: '700' as const,
        color: palette.textMuted,
      },
    }),
    [palette],
  );

  const draftDateLabel = formatAttendanceReportDateLabel(draft.attendanceDate);
  const activeChip = chipFromStatus(applied.status);

  const fetchReport = useCallback(
    async (filters: AttendanceReportFilters, isRefresh = false) => {
      if (!capabilities.canPreview) {
        setError('You do not have permission to preview this report.');
        setLoading(false);
        return;
      }

      const dateError = validateAttendanceReportDate(filters.attendanceDate);
      if (dateError) {
        setFilterError(dateError);
        setError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const registerFilters = attendanceRegisterFilters(
        filters.attendanceDate,
        filters.department,
      );
      if (!registerFilters) {
        setFilterError('Unable to resolve the selected attendance date.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const requestKey = [
        registerFilters.fromDate,
        registerFilters.toDate,
        registerFilters.departmentId ?? '',
      ].join('|');
      if (inFlightKey.current === requestKey) {
        return;
      }

      const currentRequest = ++fetchGeneration.current;
      inFlightKey.current = requestKey;
      setFilterError(null);
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const preview = isTeam
          ? await runTeamAttendanceRegisterPreview({
              fromDate: registerFilters.fromDate,
              toDate: registerFilters.toDate,
              maxRows: PREVIEW_MAX_ROWS,
            })
          : await runReportPreview({
              reportCode: REPORT_CODE,
              filters: registerFilters,
              maxRows: PREVIEW_MAX_ROWS,
            });
        if (!mountedRef.current || currentRequest !== fetchGeneration.current) {
          return;
        }
        setRows(mapAttendanceReportRows(preview.rows));
        setTruncated(preview.truncated);
        setApplied(current =>
          current.attendanceDate === filters.attendanceDate &&
          current.department === filters.department &&
          current.status === filters.status
            ? current
            : filters,
        );
        setError(null);
        lastSuccessAt.current = Date.now();
      } catch (err) {
        if (!mountedRef.current || currentRequest !== fetchGeneration.current) {
          return;
        }
        setError(
          reportErrorMessage(err, 'Unable to load employee attendance.'),
        );
      } finally {
        if (currentRequest === fetchGeneration.current) {
          inFlightKey.current = null;
          if (mountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      }
    },
    [capabilities.canPreview, isTeam],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Team register is already limited to the lead's employees; org-wide
    // location/department master data is not needed (or permitted).
    if (isTeam) return undefined;
    let cancelled = false;
    const loadLocations = async () => {
      const options: LocationOption[] = [];
      let page = 1;
      let totalPages = 1;
      while (page <= totalPages) {
        const result = await getLocations({
          page,
          pageSize: MASTER_DATA_PAGE_SIZE,
          filters: { isActive: true },
        });
        options.push(
          ...result.items.map(item => ({
            id: item.locationId,
            label: item.locationName,
          })),
        );
        totalPages = result.totalPages;
        page += 1;
      }
      if (!cancelled) {
        setLocationOptions(options);
      }
    };

    const loadDepartments = async () => {
      const options: DepartmentOption[] = [];
      let page = 1;
      let totalPages = 1;
      while (page <= totalPages) {
        const result = await getDepartments({
          page,
          pageSize: MASTER_DATA_PAGE_SIZE,
          filters: { isActive: true },
        });
        options.push(
          ...result.items.map(item => ({
            id: item.departmentId,
            label: item.departmentName,
            locationId: item.locationId,
          })),
        );
        totalPages = result.totalPages;
        page += 1;
      }
      if (!cancelled) {
        setDepartmentOptions(options);
      }
    };

    Promise.allSettled([loadLocations(), loadDepartments()]).then(results => {
      if (cancelled) return;
      if (results[0].status === 'rejected') {
        setLocationOptions([]);
      }
      if (results[1].status === 'rejected') {
        setDepartmentOptions([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isTeam]);

  useEffect(() => {
    const routedStatus = route.params?.initialStatus?.trim();
    if (!routedStatus) return;

    const filters = { ...appliedRef.current, status: routedStatus };
    setDraft(filters);
    setApplied(current =>
      current.status === filters.status ? current : filters,
    );
    if (isMoreChip(chipFromStatus(routedStatus))) {
      setShowMoreChips(true);
    }
    navigation.setParams({ initialStatus: undefined });
  }, [navigation, route.params?.initialStatus]);

  useFocusEffect(
    useCallback(() => {
      const isRefresh = hasFocused.current;
      hasFocused.current = true;
      if (
        isRefresh &&
        lastSuccessAt.current > 0 &&
        Date.now() - lastSuccessAt.current < FOCUS_RELOAD_COOLDOWN_MS
      ) {
        return;
      }
      fetchReport(appliedRef.current, isRefresh).catch(() => undefined);
    }, [fetchReport]),
  );

  const visibleDepartmentOptions = useMemo(
    () =>
      departmentOptions
        .filter(
          option =>
            !selectedLocation || option.locationId === selectedLocation.id,
        )
        .sort((left, right) => left.label.localeCompare(right.label)),
    [departmentOptions, selectedLocation],
  );

  const departmentRows = useMemo(() => {
    let next = filterAttendanceReportRows(rows, {
      department: applied.department,
      status: '',
    });
    if (selectedLocation) {
      const allowed = new Set(
        visibleDepartmentOptions.map(option =>
          option.label.trim().toLowerCase(),
        ),
      );
      next = next.filter(
        row =>
          row.department != null &&
          allowed.has(row.department.trim().toLowerCase()),
      );
    }
    return next;
  }, [applied.department, rows, selectedLocation, visibleDepartmentOptions]);

  const statusFilteredRows = useMemo(
    () =>
      filterAttendanceReportRows(departmentRows, {
        department: '',
        status: applied.status,
      }),
    [applied.status, departmentRows],
  );

  const visibleRows = useMemo(() => {
    const query = employeeQuery.trim().toLowerCase();
    if (!query) return statusFilteredRows;
    return statusFilteredRows.filter(row =>
      row.employee.toLowerCase().includes(query),
    );
  }, [employeeQuery, statusFilteredRows]);

  const summary = useMemo(
    () => summarizeAttendanceReportRows(departmentRows),
    [departmentRows],
  );

  const summaryMetrics = useMemo<SummaryMetric[]>(
    () => [
      {
        key: 'all',
        label: 'All',
        value: departmentRows.length,
        color: palette.primary,
        chip: 'all',
      },
      {
        key: 'present',
        label: 'Present',
        value: summary.present,
        color: palette.success,
        chip: 'present',
      },
      {
        key: 'absent',
        label: 'LOP',
        value: summary.absent,
        color: palette.error,
        chip: 'absent',
      },
      {
        key: 'leave',
        label: 'On Leave',
        value: summary.onLeave,
        color: palette.warning,
        chip: 'leave',
      },
      {
        key: 'wfh',
        label: 'WFH',
        value: summary.wfh,
        color: palette.info,
        chip: 'wfh',
      },
      {
        key: 'late',
        label: 'Late',
        value: summary.late,
        color: palette.warning,
        chip: 'late',
      },
      {
        key: 'shortHours',
        label: 'Short Hours',
        value: summary.shortHours,
        color: palette.warning,
        chip: 'shortHours',
      },
    ],
    [
      departmentRows.length,
      palette.error,
      palette.info,
      palette.primary,
      palette.success,
      palette.warning,
      summary,
    ],
  );

  const primaryChips = useMemo(
    () =>
      [
        { id: 'all' as const, label: `All (${departmentRows.length})` },
        { id: 'present' as const, label: `Present (${summary.present})` },
        { id: 'absent' as const, label: `LOP (${summary.absent})` },
      ] as const,
    [departmentRows.length, summary.absent, summary.present],
  );

  const moreChips = useMemo(
    () =>
      [
        { id: 'late' as const, label: `Late (${summary.late})` },
        { id: 'leave' as const, label: `On Leave (${summary.onLeave})` },
        { id: 'wfh' as const, label: `WFH (${summary.wfh})` },
        {
          id: 'shortHours' as const,
          label: `Short Hours (${summary.shortHours})`,
        },
        {
          id: 'missingCheckout' as const,
          label: `Missing Checkout (${summary.missingCheckout})`,
        },
      ] as const,
    [
      summary.late,
      summary.missingCheckout,
      summary.onLeave,
      summary.shortHours,
      summary.wfh,
    ],
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.includes(`${REPORT_CODE}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads],
  );

  const updateDraft = (field: keyof AttendanceReportFilters, value: string) => {
    setDraft(current => ({ ...current, [field]: value }));
    setFilterError(null);
    setDownloadError(null);
  };

  const applyFilters = () => {
    fetchReport({
      attendanceDate: draft.attendanceDate.trim(),
      department: draft.department.trim(),
      status: draft.status.trim(),
    }).catch(() => undefined);
  };

  const changeDateBy = (deltaDays: number) => {
    const nextDate = shiftAttendanceDate(draft.attendanceDate, deltaDays);
    const next = {
      ...draft,
      attendanceDate: nextDate,
    };
    setDraft(next);
    setFilterError(null);
    setDownloadError(null);
    fetchReport(next).catch(() => undefined);
  };

  const selectStatusChip = (chip: StatusChipId) => {
    if (isMoreChip(chip)) {
      setShowMoreChips(true);
    }
    const nextStatus = STATUS_CHIP_FILTER[chip];
    setDraft(current => ({ ...current, status: nextStatus }));
    setApplied(current =>
      current.status === nextStatus
        ? current
        : { ...current, status: nextStatus },
    );
  };

  const selectDepartment = (option: DepartmentOption | null) => {
    setSelectedDepartmentLabel(option?.label ?? null);
    setOpenPicker(null);
    const nextDepartment = option ? String(option.id) : '';
    const next = { ...draft, department: nextDepartment };
    setDraft(next);
    fetchReport(next).catch(() => undefined);
  };

  const selectLocation = (option: LocationOption | null) => {
    setSelectedLocation(option);
    setOpenPicker(null);
    if (
      option &&
      selectedDepartmentLabel &&
      !departmentOptions.some(
        department =>
          department.label === selectedDepartmentLabel &&
          department.locationId === option.id,
      )
    ) {
      selectDepartment(null);
    }
  };

  const exportReport = async (format: 'CSV' | 'XLSX') => {
    setDownloadError(null);
    const next = {
      attendanceDate: draft.attendanceDate.trim(),
      department: draft.department.trim(),
      status: draft.status.trim(),
    };
    const dateError = validateAttendanceReportDate(next.attendanceDate);
    if (dateError) {
      setFilterError(dateError);
      return;
    }
    const registerFilters = attendanceRegisterFilters(
      next.attendanceDate,
      next.department,
    );
    if (!registerFilters) {
      setFilterError('Unable to resolve the selected attendance date.');
      return;
    }

    const allowed =
      format === 'CSV' ? capabilities.canExportCsv : capabilities.canExportXlsx;
    if (!allowed) {
      setDownloadError('You do not have permission to export reports.');
      return;
    }
    if (downloads.isDownloadInFlight(REPORT_CODE, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }

    try {
      const result = await downloads.startDownload({
        reportCode: REPORT_CODE,
        reportName: REPORT_NAME,
        format,
        filters: {
          fromDate: registerFilters.fromDate,
          toDate: registerFilters.toDate,
          ...(registerFilters.departmentId
            ? { departmentId: Number(registerFilters.departmentId) }
            : {}),
        },
        view: {
          columns: [
            { key: 'employee', label: 'Employee Name' },
            { key: 'checkIn', label: 'Check In' },
            { key: 'checkOut', label: 'Check Out' },
            { key: 'workHours', label: 'Work Hours' },
            { key: 'breakTime', label: 'Break' },
            { key: 'expectedHours', label: 'Expected Work Hours' },
            { key: 'totalHours', label: 'Total Work Hours' },
            { key: 'status', label: 'Status' },
            { key: 'dayDetails', label: isTimePermissionEnabled() ? 'Leave / Permission' : 'Leave' },
          ],
          rows: visibleRows.map(row => ({
            employee: row.employee,
            checkIn: formatPunchTimeWithSource(
              row.checkIn,
              row.checkInSourceLabel,
            ),
            checkOut: formatPunchTimeWithSource(
              row.checkOut,
              row.checkOutSourceLabel,
            ),
            workHours: row.workHours,
            breakTime: row.breakTime,
            expectedHours: row.expectedHours,
            totalHours: row.totalHours,
            status: row.status,
            dayDetails: row.dayDetails,
          })),
        },
      });
      (
        navigation as unknown as NativeStackNavigationProp<RootStackParamList>
      ).navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(
        reportErrorMessage(err, 'The report could not be saved.'),
      );
    }
  };

  const departmentButtonLabel = selectedDepartmentLabel ?? 'All Departments';
  const locationButtonLabel = selectedLocation?.label ?? 'All Locations';

  const allowed =
    hasPermissionAccess ||
    (!isTeam &&
      isReportGranted(
        roleReportAccess.access,
        REPORT_ACCESS_CODES.DAILY_ATTENDANCE,
      ));
  if (!allowed) {
    return roleReportAccess.loading ? (
      <AppScreen>
        <AppLoader message="Loading report" />
      </AppScreen>
    ) : (
      <UnauthorizedScreen />
    );
  }

  return (
        <AppScreen
          scroll
          keyboardSafe
          webLayout="wide"
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => fetchReport(applied, true)}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader>
            <View style={styles.headerRow}>
              {canGoBack ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Back"
                  onPress={() => navigation.goBack()}
                  hitSlop={8}
                  style={styles.headerSide}
                >
                  <Text style={[styles.headerGlyph, { color: palette.white }]}>
                    ‹
                  </Text>
                </Pressable>
              ) : (
                <View style={styles.headerSide} />
              )}
              <View style={styles.headerCenter}>
                <Text
                  accessibilityRole="header"
                  numberOfLines={1}
                  style={[styles.headerTitle, { color: palette.white }]}
                >
                  {isTeam ? TEAM_REPORT_NAME : REPORT_NAME}
                </Text>
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Filters"
                accessibilityState={{ expanded: showAdvancedFilters }}
                onPress={() => setShowAdvancedFilters(current => !current)}
                hitSlop={8}
                style={styles.headerSideEnd}
              >
                <FilterIcon color={palette.white} />
              </Pressable>
            </View>
          </ScreenHeader>

          <View style={styles.body}>
            <View
              style={[
                styles.dateBar,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                },
              ]}
            >
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Previous day"
                onPress={() => changeDateBy(-1)}
                hitSlop={8}
                style={styles.dateNavButton}
              >
                <ChevronIcon color={palette.primary} direction="left" />
              </Pressable>
              <Text
                style={[styles.dateLabel, { color: palette.textPrimary }]}
                numberOfLines={1}
              >
                {draftDateLabel}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Next day"
                onPress={() => changeDateBy(1)}
                hitSlop={8}
                style={styles.dateNavButton}
              >
                <ChevronIcon color={palette.primary} direction="right" />
              </Pressable>
            </View>

            {showAdvancedFilters ? (
              <View
                style={[
                  styles.advancedCard,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                    shadowColor: palette.black,
                  },
                ]}
              >
                <AppDateField
                  label="Attendance date"
                  value={draft.attendanceDate}
                  onChange={value => updateDraft('attendanceDate', value)}
                />
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.chipRow}
                >
                  {primaryChips.map(chip => {
                    const selected = activeChip === chip.id;
                    return (
                      <Pressable
                        key={chip.id}
                        accessibilityRole="button"
                        accessibilityLabel={chip.label}
                        accessibilityState={{ selected }}
                        onPress={() => selectStatusChip(chip.id)}
                        style={[
                          styles.chip,
                          {
                            backgroundColor: palette.surface,
                            borderColor: selected
                              ? palette.primary
                              : palette.border,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.chipLabel,
                            {
                              color: selected
                                ? palette.primary
                                : palette.textPrimary,
                            },
                          ]}
                        >
                          {chip.label}
                        </Text>
                      </Pressable>
                    );
                  })}
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="More status filters"
                    accessibilityState={{ expanded: showMoreChips }}
                    onPress={() => setShowMoreChips(current => !current)}
                    style={[
                      styles.chip,
                      {
                        backgroundColor: palette.surface,
                        borderColor:
                          showMoreChips || isMoreChip(activeChip)
                            ? palette.primary
                            : palette.border,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.chipLabel,
                        {
                          color:
                            showMoreChips || isMoreChip(activeChip)
                              ? palette.primary
                              : palette.textPrimary,
                        },
                      ]}
                    >
                      More
                    </Text>
                  </Pressable>
                  {showMoreChips
                    ? moreChips.map(chip => {
                        const selected = activeChip === chip.id;
                        return (
                          <Pressable
                            key={chip.id}
                            accessibilityRole="button"
                            accessibilityLabel={chip.label}
                            accessibilityState={{ selected }}
                            onPress={() => selectStatusChip(chip.id)}
                            style={[
                              styles.chip,
                              {
                                backgroundColor: palette.surface,
                                borderColor: selected
                                  ? palette.primary
                                  : palette.border,
                              },
                            ]}
                          >
                            <Text
                              style={[
                                styles.chipLabel,
                                {
                                  color: selected
                                    ? palette.primary
                                    : palette.textPrimary,
                                },
                              ]}
                            >
                              {chip.label}
                            </Text>
                          </Pressable>
                        );
                      })
                    : null}
                </ScrollView>
                {filterError ? (
                  <Text accessibilityRole="alert" style={themed.error}>
                    {filterError}
                  </Text>
                ) : null}
                <AppButton title="Apply" onPress={applyFilters} />
              </View>
            ) : null}

            {filterError && !showAdvancedFilters ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {filterError}
              </Text>
            ) : null}
            {downloadError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {downloadError}
              </Text>
            ) : null}

            <View style={styles.summaryRow}>
              {summaryMetrics.map(metric => {
                const selected = activeChip === metric.chip;
                return (
                  <Pressable
                    key={metric.key}
                    accessibilityRole="button"
                    accessibilityLabel={`${metric.label}: ${metric.value}`}
                    accessibilityState={{ selected }}
                    onPress={() => selectStatusChip(metric.chip)}
                    style={[
                      styles.summaryCard,
                      {
                        backgroundColor: palette.surface,
                        borderColor: selected ? metric.color : palette.border,
                        shadowColor: palette.black,
                      },
                    ]}
                  >
                    <Text
                      style={[styles.summaryValue, { color: metric.color }]}
                    >
                      {metric.value}
                    </Text>
                    <Text
                      style={[
                        styles.summaryLabel,
                        { color: palette.textSecondary },
                      ]}
                      numberOfLines={1}
                    >
                      {metric.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            {isTeam ? null : (
            <View style={styles.filterRow}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={departmentButtonLabel}
                accessibilityState={{ expanded: openPicker === 'department' }}
                onPress={() =>
                  setOpenPicker(current =>
                    current === 'department' ? null : 'department',
                  )
                }
                style={[
                  styles.dropdownButton,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Text
                  style={[styles.dropdownLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {departmentButtonLabel}
                </Text>
                <Text style={{ color: palette.textMuted }}>▾</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={locationButtonLabel}
                accessibilityState={{ expanded: openPicker === 'location' }}
                onPress={() =>
                  setOpenPicker(current =>
                    current === 'location' ? null : 'location',
                  )
                }
                style={[
                  styles.dropdownButton,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Text
                  style={[styles.dropdownLabel, { color: palette.textPrimary }]}
                  numberOfLines={1}
                >
                  {locationButtonLabel}
                </Text>
                <Text style={{ color: palette.textMuted }}>▾</Text>
              </Pressable>
            </View>
            )}

            {openPicker === 'department' ? (
              <View
                style={[
                  styles.pickerPanel,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="All Departments"
                  onPress={() => selectDepartment(null)}
                  style={styles.pickerOption}
                >
                  <Text style={{ color: palette.textPrimary }}>
                    All Departments
                  </Text>
                </Pressable>
                {visibleDepartmentOptions.map(option => (
                  <Pressable
                    key={option.id}
                    accessibilityRole="button"
                    accessibilityLabel={option.label}
                    onPress={() => selectDepartment(option)}
                    style={styles.pickerOption}
                  >
                    <Text style={{ color: palette.textPrimary }}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            ) : null}

            {openPicker === 'location' ? (
              <View
                style={[
                  styles.pickerPanel,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="All Locations"
                  onPress={() => selectLocation(null)}
                  style={styles.pickerOption}
                >
                  <Text style={{ color: palette.textPrimary }}>
                    All Locations
                  </Text>
                </Pressable>
                {locationOptions.map(option => (
                  <Pressable
                    key={option.id}
                    accessibilityRole="button"
                    accessibilityLabel={option.label}
                    onPress={() => selectLocation(option)}
                    style={styles.pickerOption}
                  >
                    <Text style={{ color: palette.textPrimary }}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            ) : null}

            <View
              style={[
                styles.searchField,
                {
                  backgroundColor: palette.surface,
                  borderColor: palette.border,
                },
              ]}
            >
              <SearchIcon color={palette.textMuted} />
              <TextInput
                accessibilityLabel="Search employee"
                value={employeeQuery}
                onChangeText={setEmployeeQuery}
                placeholder="Search employee..."
                placeholderTextColor={palette.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                style={[styles.searchInput, { color: palette.textPrimary }]}
              />
            </View>

            {activeProgress ? (
              <ReportDownloadProgressView
                progress={activeProgress}
                canCancel={Boolean(activeProgress)}
                onCancel={() =>
                  activeProgress
                    ? downloads.cancelDownload(activeProgress.downloadId)
                    : undefined
                }
              />
            ) : null}

            {loading && rows.length === 0 ? (
              <AppLoader message="Loading employee attendance" />
            ) : error && rows.length === 0 ? (
              <ErrorState
                message={error}
                onRetry={() => fetchReport(applied)}
              />
            ) : visibleRows.length === 0 ? (
              <EmptyState
                title="No data available"
                message={
                  applied.status
                    ? `No employees matched ${applied.status} for the selected date.`
                    : 'No employee attendance rows matched the selected date.'
                }
                actionTitle="Retry"
                onAction={() => fetchReport(applied)}
              />
            ) : (
              <View
                style={[
                  styles.tableCard,
                  {
                    backgroundColor: palette.surface,
                    borderColor: palette.border,
                  },
                ]}
              >
                {truncated ? (
                  <Text style={themed.warning}>
                    Showing the first {PREVIEW_MAX_ROWS} rows. Export for the
                    full file.
                  </Text>
                ) : null}
                <AttendanceReportTable
                  rows={visibleRows}
                  palette={palette}
                  headerCellStyle={themed.headerCell}
                  onOpenTracking={attendanceId =>
                    (
                      navigation as unknown as NativeStackNavigationProp<RootStackParamList>
                    ).navigate(routes.locationTrackingDetails, { attendanceId })
                  }
                />
              </View>
            )}

            {capabilities.canExportCsv || capabilities.canExportXlsx ? (
              <View style={styles.exportRow}>
                {capabilities.canExportCsv ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Export PDF"
                    accessibilityState={{
                      disabled: Boolean(activeProgress),
                      busy: downloads.isDownloadInFlight(REPORT_CODE, 'CSV'),
                    }}
                    disabled={Boolean(activeProgress)}
                    onPress={() => {
                      exportReport('CSV').catch(() => undefined);
                    }}
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: palette.surface,
                        borderColor: palette.primary,
                        opacity: activeProgress ? 0.45 : 1,
                      },
                    ]}
                  >
                    <Text
                      style={[styles.exportLabel, { color: palette.primary }]}
                    >
                      Export PDF
                    </Text>
                  </Pressable>
                ) : null}
                {capabilities.canExportXlsx ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Export Excel"
                    accessibilityState={{
                      disabled: Boolean(activeProgress),
                      busy: downloads.isDownloadInFlight(REPORT_CODE, 'XLSX'),
                    }}
                    disabled={Boolean(activeProgress)}
                    onPress={() => {
                      exportReport('XLSX').catch(() => undefined);
                    }}
                    style={[
                      styles.exportButton,
                      {
                        backgroundColor: palette.surface,
                        borderColor: palette.primary,
                        opacity: activeProgress ? 0.45 : 1,
                      },
                    ]}
                  >
                    <Text
                      style={[styles.exportLabel, { color: palette.primary }]}
                    >
                      Export Excel
                    </Text>
                  </Pressable>
                ) : null}
              </View>
            ) : null}
          </View>
        </AppScreen>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerSide: {
    width: 40,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  headerSideEnd: {
    width: 40,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerGlyph: {
    fontSize: 32,
    lineHeight: 34,
    fontWeight: '300',
  },
  headerTitle: {
    ...typography.sectionTitle,
    textAlign: 'center',
    fontWeight: '700',
  },
  filterIcon: {
    width: 18,
    height: 14,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  filterLine: {
    height: 2,
    borderRadius: 1,
  },
  filterWide: { width: 18 },
  filterMid: { width: 12 },
  filterNarrow: { width: 6 },
  body: { marginTop: spacing.lg, gap: spacing.md },
  dateBar: {
    minHeight: 48,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dateNavButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chevron: {
    fontSize: 28,
    lineHeight: 30,
    fontWeight: '600',
  },
  dateLabel: {
    ...typography.body,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
  },
  advancedCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.sm,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  summaryCard: {
    width: '31%',
    minHeight: 84,
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.07,
    shadowRadius: 12,
    elevation: 2,
  },
  summaryValue: {
    ...typography.screenTitle,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: '700',
  },
  summaryLabel: {
    ...typography.caption,
    fontWeight: '600',
    textAlign: 'center',
  },
  filterRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  dropdownButton: {
    flex: 1,
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  dropdownLabel: {
    ...typography.caption,
    fontWeight: '600',
    flex: 1,
  },
  pickerPanel: {
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
  },
  pickerOption: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  searchField: {
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchIcon: {
    width: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 1.5,
  },
  searchHandle: {
    position: 'absolute',
    width: 6,
    height: 1.5,
    borderRadius: 1,
    right: 1,
    bottom: 2,
    transform: [{ rotate: '45deg' }],
  },
  searchInput: {
    ...typography.body,
    flex: 1,
    paddingVertical: spacing.sm,
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingRight: spacing.sm,
  },
  chip: {
    borderWidth: 1.5,
    borderRadius: 12,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipLabel: {
    ...typography.caption,
    fontWeight: '700',
  },
  tableCard: {
    borderWidth: 1,
    borderRadius: 16,
    overflow: 'hidden',
    padding: spacing.sm,
  },
  tableFrame: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  frozenPane: {
    zIndex: 2,
    borderRightWidth: StyleSheet.hairlineWidth,
  },
  scrollPane: {
    flex: 1,
    minWidth: 0,
  },
  headerRowTable: {
    flexDirection: 'row',
    borderRadius: 10,
    alignItems: 'center',
  },
  headerCellPad: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
  },
  bodyRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    minHeight: 64,
  },
  employeeCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  employeeName: {
    ...typography.caption,
    fontWeight: '600',
    flexShrink: 1,
  },
  cellText: {
    ...typography.caption,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.md,
  },
  statusText: {
    fontWeight: '700',
  },
  timeCell: {
    width: COL_TIME,
  },
  trackingLink: {
    ...typography.caption,
    fontWeight: '700',
    paddingHorizontal: spacing.sm,
  },
  trackingStatus: {
    ...typography.caption,
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
  },
  exportRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.xl,
  },
  exportButton: {
    flex: 1,
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  exportLabel: {
    ...typography.button,
    fontWeight: '700',
  },
});
